import { useHandleHttpError } from '../../../../../lib/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import type { GenerateCertificateProps } from './types';
import * as Yup from 'yup';
import { BulmaType, ButtonProps, DatePicker, Form, Input, TaskPanel } from '@easylife365/react';
import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useGenerateSamlCertificate } from '../../../../../api/app.api';
import { GenerateSamlCertificateRequest } from '../../../../../api/app.types';

const MAX_YEARS = 3;

const minDate = new Date();
minDate.setUTCHours(23, 59, 59, 0);

const maxDate = new Date();
maxDate.setFullYear(maxDate.getFullYear() + MAX_YEARS);
maxDate.setUTCHours(23, 59, 59, 0);

const defaultExpiration = new Date();
defaultExpiration.setFullYear(defaultExpiration.getFullYear() + 2);

const initialValues: GenerateSamlCertificateRequest = {
  displayName: 'CN=Microsoft Azure Federated SSO Certificate',
  endDateTime: new Date(defaultExpiration),
};

const GenerateCertificate: React.FC<GenerateCertificateProps> = ({ app, isOpen, close }) => {
  const { t } = useTranslation('addCertificatePanel');
  const { t: tCommon } = useTranslation('common');
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        endDateTime: Yup.date()
          .required(tCommon('validation.expirationRequired'))
          .min(minDate, tCommon('validation.expirationPast'))
          .max(maxDate, tCommon('validation.expirationMax', { years: MAX_YEARS })),
      }),
    [tCommon]
  );
  const handleHttpError = useHandleHttpError();
  const { mutateAsync: generateSamlCertificate } = useGenerateSamlCertificate(app.resourceType);

  const onSubmit = async (values: GenerateSamlCertificateRequest) => {
    try {
      await generateSamlCertificate({
        id: app.id,
        request: values,
      });
      close();
    } catch (error) {
      handleHttpError(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ isSubmitting, isValid, handleSubmit }) => {
        const ctaButtons: ButtonProps[] = [
          {
            bulmaType: BulmaType.Primary,
            disabled: !isValid || isSubmitting,
            text: t('buttons.generate'),
            icon: faWandMagicSparkles,
            loading: isSubmitting,
            type: 'submit',
          },
        ];

        return (
          <form onSubmit={handleSubmit}>
            <TaskPanel title={t('generate.title')} isVisible={isOpen} ctaButtons={ctaButtons} onCancel={close}>
              <Form transparent={true}>
                <DatePicker
                  formLabel={tCommon('fields.expiration')}
                  minDate={minDate}
                  maxDate={maxDate}
                  name="endDateTime"
                  required
                />
                <Input
                  formLabel={tCommon('fields.description')}
                  name="displayName"
                  placeholder={t('fields.descriptionPlaceholder')}
                />
              </Form>
            </TaskPanel>
          </form>
        );
      }}
    </Formik>
  );
};

export default GenerateCertificate;
